/**
 * Copie dans le presse-papiers avec retour visuel.
 *
 * `navigator.clipboard` n'est disponible qu'en contexte sécurisé ; en
 * développement sur `http://localhost` il l'est, mais pas derrière une IP
 * nue — d'où le repli par `document.execCommand`, seul moyen restant.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Durée d'affichage de la confirmation « Copié ». */
const FEEDBACK_MS = 2000;

/** État de la copie : au repos, réussie, ou en échec. */
export type CopyStatus = 'idle' | 'copied' | 'error';

/** Copie un texte, en tentant l'API moderne puis le repli historique. */
async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // On tente le repli plutôt que d'abandonner.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

/** Retourne l'état courant et la fonction de copie. */
export function useClipboard(): { status: CopyStatus; copy: (text: string) => void } {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback((text: string) => {
    void copyText(text).then((ok) => {
      setStatus(ok ? 'copied' : 'error');
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setStatus('idle'), FEEDBACK_MS);
    });
  }, []);

  return { status, copy };
}
